import ReactECharts from 'echarts-for-react';
import benchmark from '../../data/promptEngineeringBenchmark.json';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * JudgeHeatmapChart — blog12, interactive item 3: all 29 judge dimensions × 5 arms.
 *
 * Chart-type-first: 145 values across two categorical axes → heatmap. A grouped
 * bar chart at this density is unreadable, and the interesting content is WHERE
 * the arms differ, not just the totals.
 *
 * Sequential single-hue ramp (Raisy teal, light→dark) because the value is a
 * magnitude, never a rainbow. Hover gives the exact score; ONE shared formatter
 * feeds the cell label, the tooltip, and the visualMap ticks.
 */

const TEAL_RAMP = ['#E6F7F9', '#B3E8ED', '#66D1DB', '#1FBECB', '#0AB3BF', '#087B8B'];

type Arm = { key: string; label: string; isRaisy: boolean; score: number; dimensionScores: Record<string, number> };

const fmt = (v: number) => `${v}`;

/** Dimension order = the judge rubric's own order (data file preserves it). */
const dimensionsOf = (arms: Arm[]): string[] => Object.keys(arms[0].dimensionScores);

const pretty = (d: string) => d.replace(/_/g, ' ');

export const JudgeHeatmapChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const { arms, max_score: maxScore } = benchmark as unknown as {
    arms: Arm[];
    max_score: number;
  };
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const dims = dimensionsOf(arms);

  // Phone: the y-axis labels are the space problem — `no_fabricated_entities` at
  // 10.5px needs ~150px of gutter, over 40% of a 343px column. Shorten the labels
  // and the gutter together, drop the in-cell numbers (hover still gives them),
  // and grow the plot vertically so 29 rows stay tappable.
  const gutter = narrow ? 96 : 168;
  const chartHeight = height ?? (narrow ? 700 : 620);
  const shorten = (d: string) =>
    narrow && d.length > 15 ? `${d.slice(0, 14)}…` : d;

  const C = isDark
    ? { bg: '#0F172A', axis: '#E2E8F0', muted: '#94A3B8', grid: '#334155', card: 'bg-slate-900', border: 'border-slate-800', caption: 'text-slate-400' }
    : { bg: '#FFFFFF', axis: '#334155', muted: '#64748B', grid: '#334155', card: 'bg-white', border: 'border-slate-200', caption: 'text-slate-500' };

  // [armIndex, dimIndex, score]
  const data = arms.flatMap((a, ai) =>
    dims.map((d, di) => [ai, di, a.dimensionScores[d]] as [number, number, number]),
  );

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { data: [number, number, number] }) =>
        `${arms[p.data[0]].label}<br/>${pretty(dims[p.data[1]])}: <b>${fmt(p.data[2])}</b> / 10`,
    },
    grid: { left: gutter, right: narrow ? 8 : 24, top: narrow ? 56 : 44, bottom: 16 },
    xAxis: {
      type: 'category',
      position: 'top',
      // Phone: "Professional System Prompt" cannot fit a ~50px column, so the
      // adjective alone identifies the arm and the footer legend spells it out.
      data: arms.map((a) =>
        narrow
          ? a.label.replace(' System Prompt', '').replace('Raisy Memory', 'Raisy')
          : a.label.replace(' System Prompt', '\nSystem Prompt'),
      ),
      splitArea: { show: true },
      axisLabel: {
        color: C.axis,
        fontSize: narrow ? 9.5 : 11,
        lineHeight: 14,
        interval: 0,
        rotate: narrow ? 30 : 0,
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: dims.map((d) => shorten(pretty(d))),
      splitArea: { show: true },
      axisLabel: { color: C.muted, fontSize: narrow ? 9 : 10.5 },
      axisLine: { show: false },
      axisTick: { show: false },
      inverse: true,
    },
    visualMap: {
      min: 1,
      max: 10,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: -6,
      itemWidth: 12,
      itemHeight: 90,
      textStyle: { color: C.muted, fontSize: 11 },
      formatter: (v: number) => fmt(v),
      inRange: { color: TEAL_RAMP },
      show: false,
    },
    series: [
      {
        name: 'Judge score',
        type: 'heatmap',
        data,
        label: {
          // In-cell numbers do not fit a ~48px phone column; the tooltip carries
          // them instead, so the mobile view reads as a pattern and stays exact on tap.
          show: !narrow,
          color: '#0F172A',
          fontSize: 10,
          fontWeight: 600,
          formatter: (p: { data: [number, number, number] }) => fmt(p.data[2]),
        },
        itemStyle: { borderColor: C.bg, borderWidth: 2 },
        emphasis: { itemStyle: { borderColor: '#0AB3BF', borderWidth: 2 } },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height: chartHeight }} opts={{ renderer: 'canvas' }} />
      <div className={`px-3 pb-1 flex flex-wrap justify-center gap-x-4 gap-y-1 text-label-xs ${C.caption}`}>
        {arms.map((a) => (
          <span key={a.key}>
            {a.label}:{' '}
            <strong className={a.isRaisy ? 'text-cyan-600 dark:text-cyan-400' : ''}>{a.score}</strong>
            /{maxScore}
          </span>
        ))}
      </div>
      <p className={`mt-1 px-3 pb-3 text-xs ${C.caption} text-center`}>
        Figure 2. Every judge dimension, every arm — darker is a higher score. Raisy Memory leads on
        the situation-dependent rows (specificity, no omissions, context usage, completeness) and
        trails badly on <em>format compliance</em> and <em>instruction adherence</em>, where the
        judge penalised its JSON envelope. Both are shown rather than netted out. Single sample per arm.
      </p>
    </div>
  );
};
